'use client';

import { useParams } from 'next/navigation';
import React, { useState } from 'react';

import { StrapiCityDatum, StrapiStop } from '@/apis/strapi';
import { doTranslation } from '@/app/i18n/client';
import BusStationCards from '@/components/bus-station/bus-station-cards';
import BusStationMap from '@/components/bus-station/bus-station-map';
import styles from '@/components/bus-station/bus-station-nearby-stations.module.scss';
import Button from '@/components/global/buttons/button';

type Props = {
  cityData: StrapiCityDatum;
};

const BusStationNearbyStations = (props: Props): React.JSX.Element => {
  const { lng }: any = useParams();
  const { t } = doTranslation(lng);
  const cityData = props.cityData.attributes;
  let stops = cityData.stops?.data || [];
  const state = cityData.state?.data.attributes || null;

  // Move main stop to front of list (main: stop slug = city slug)
  if (stops.length) {
    const preferredFirstStopSlug = cityData.city_slug;
    stops.sort(function (stop1, stop2) {
      return stop1.attributes.stop_slug == preferredFirstStopSlug
        ? -1
        : stop2.attributes.stop_slug == preferredFirstStopSlug
          ? 1
          : 0;
    });
  }
  // Tack on nearby stops to end of list
  if (cityData.nearby_stops) {
    stops = stops.concat(cityData.nearby_stops.data);
  }

  const [showLoadMore, setShowLoadMore] = useState(stops.length > 5);
  const [markerIndex, setMarkerIndex] = useState<number | null>(null);

  const cityName = cityData.city_name;
  const stateName = state?.state_name;

  const cityNameStateName =
    cityName && stateName
      ? t('{{ cityName }}, {{ stateCode }} Bus Stations', { cityName: cityName, stateCode: stateName })
      : null;

  return (
    <div className={styles.container}>
      <div className={styles.cardContainer}>
        {cityNameStateName && (
          <h2>{cityNameStateName}</h2>
        )}
        <div className={styles.cards}>
          <BusStationCards
            cityName={props.cityData.attributes.city_name || ''}
            setMarkerIndex={(index: number | null) => setMarkerIndex(index)}
            showLoadMore={showLoadMore}
            state={state}
            stops={showLoadMore ? stops.slice(0, 5) : stops}
          />
          {showLoadMore && (
            <div className={styles.more}>
              <div>
                <span className={styles.text}>{t('Showing')} </span>
                <span className={styles.count}>5</span>
                <span className={styles.text}> {t('out of')} </span>
                <span className={styles.count}>{stops.length}</span>
              </div>
              <Button onClick={() => setShowLoadMore(false)} primary>
                {t('Load More')}
              </Button>
            </div>
          )}
        </div>
      </div>
      <BusStationMap
        details={stops && stops.length === 1 ? true : false}
        markerIndex={markerIndex}
        state={props.cityData?.attributes?.state?.data?.attributes?.state_id || null}
        stop={props.cityData?.attributes?.city_name || null}
        stops={stops || null}
        title={t('Map of Bus Stations in {{ city }}, {{ state }}', {
          city: props.cityData?.attributes?.city_name,
          state: props.cityData?.attributes?.state?.data?.attributes?.state_id
        })}
      />
    </div>
  );
};

export default BusStationNearbyStations;
